import { formatDateTime } from '@/lib/date-time';
/**
 * @file components/staff-profile/DocumentsTab.tsx
 * @description 電子檔案分頁：簽名檔 + 員工電子文件。
 *
 * 兩個區塊：
 *  1. 簽名檔區：
 *     - 若 profile.signature 有值，顯示預覽/下載按鈕
 *     - 上傳新簽名（uploadFile scope='signature'，上傳後 PUT /:id/signature { fileId }）
 *     - 清除簽名（PUT /:id/signature { fileId: null }）
 *
 *  2. 電子檔案區：
 *     - 列表展示 profile.documents（檔名/大小/上傳時間 + 下載 + 刪除）
 *     - 上傳（uploadFile scope='staff-doc'，上傳後 POST /:id/documents { fileId }）
 *     - 刪除（DELETE /:id/documents/:fid）
 */

import { useState } from 'react';
import { Button, Popconfirm, Progress, Space, Table, Typography, Upload } from 'antd';
import { message } from '@/lib/antd-app';
import { Download, Eye, FileSignature, FileText, Plus, Trash2, X } from 'lucide-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import { uploadFile } from '@/lib/file-upload';
import { downloadFile, previewFile } from '@/lib/file-preview';
import { SectionCard } from '@/components/shell';
import { color } from '@/styles/tokens';
import type { ColumnsType } from 'antd/es/table';
import type { RcFile } from 'antd/es/upload';

const { Text } = Typography;

// ─── 型別 ─────────────────────────────────────────────────────────────────────

/** 簽名檔記錄（後端回傳，為 files 表簡化視圖） */
export interface SignatureFile {
  id: number;
  filename: string;
  sizeBytes: number;
  createdAt: string;
}

/** 員工電子文件記錄（後端回傳，為 files 表簡化視圖） */
export interface StaffDocument {
  id: number;
  filename: string;
  sizeBytes: number;
  createdAt: string;
}

// ─── 工具函式 ──────────────────────────────────────────────────────────────────

/** 將位元組數轉為人類可讀的容量標示（如 1.2 MB） */
function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// ─── 組件 Props ───────────────────────────────────────────────────────────────

export interface DocumentsTabProps {
  /** 員工 userId */
  userId: number;
  /** 當前簽名檔（可能為 null） */
  signature: SignatureFile | null;
  /** 員工電子文件清單 */
  documents: StaffDocument[];
}

// ─── 簽名檔區塊 ───────────────────────────────────────────────────────────────

/**
 * 簽名檔子組件。
 * 上傳時先走 OSS 三步直傳，再 PUT /signature 更新資料庫。
 */
function SignatureSection({
  userId,
  signature,
}: {
  userId: number;
  signature: SignatureFile | null;
}) {
  const qc = useQueryClient();
  // 記錄上傳進度（0-100），null 表示未在上傳中
  const [uploadPct, setUploadPct] = useState<number | null>(null);

  /** 綁定簽名 Mutation */
  const setSignature = useMutation({
    mutationFn: (fileId: number | null) =>
      apiClient.put(`/api/settings/staff/${userId}/signature`, { fileId }),
    onSuccess: () => {
      message.success(signature ? '簽名檔已更新' : '簽名檔已設定');
      void qc.invalidateQueries({ queryKey: ['staff-profile', userId] });
    },
    onError: (err) => {
      message.error(err instanceof Error ? err.message : '設定簽名失敗');
    },
  });

  /**
   * 處理簽名上傳：
   * 1. OSS 三步直傳（uploadFile）
   * 2. 取得 fileId 後 PUT /signature { fileId }
   */
  async function handleUpload(file: RcFile) {
    setUploadPct(0);
    try {
      const uploaded = await uploadFile(file, {
        scope: 'signature',
        ownerType: 'staff',
        ownerId: userId,
        onProgress: (pct) => setUploadPct(pct),
      });
      setSignature.mutate(uploaded.id);
    } catch (err) {
      message.error(err instanceof Error ? err.message : '上傳失敗');
    } finally {
      setUploadPct(null);
    }
    // 返回 false 阻止 antd Upload 的預設行為（已由 customRequest 接管）
    return false;
  }

  return (
    <SectionCard title="簽名檔" icon={<FileSignature size={14} />}>
      <Space direction="vertical" size={12} style={{ display: 'flex' }}>
        {/* 若已有簽名檔：顯示檔名 + 預覽 + 下載 + 清除 */}
        {signature ? (
          <Space size={8} wrap>
            <Text type="secondary" style={{ fontFamily: 'monospace', fontSize: 13 }}>
              {signature.filename}
            </Text>
            <Text type="secondary" style={{ fontSize: 12 }}>
              （{formatBytes(signature.sizeBytes)}）
            </Text>
            <Button
              size="small"
              icon={<Eye size={13} />}
              onClick={() => void previewFile(signature.id)}
            >
              預覽
            </Button>
            <Button
              size="small"
              icon={<Download size={13} />}
              onClick={() => void downloadFile(signature.id)}
            >
              下載
            </Button>
            <Popconfirm
              title="確定清除簽名檔？"
              onConfirm={() => setSignature.mutate(null)}
              okText="清除"
              cancelText="取消"
              okButtonProps={{ danger: true }}
            >
              <Button size="small" danger icon={<X size={13} />} loading={setSignature.isPending}>
                清除
              </Button>
            </Popconfirm>
          </Space>
        ) : (
          <Text type="secondary">尚未設定簽名檔</Text>
        )}

        {/* 上傳進度條（上傳中才顯示） */}
        {uploadPct !== null && (
          <Progress percent={Math.round(uploadPct)} size="small" status="active" />
        )}

        {/* 上傳按鈕 */}
        <Upload
          showUploadList={false}
          beforeUpload={(file) => {
            void handleUpload(file);
            return false; // 阻止預設上傳
          }}
          accept="image/*,.pdf"
        >
          <Button
            icon={<Plus size={13} />}
            disabled={uploadPct !== null}
            loading={setSignature.isPending}
          >
            {signature ? '更換簽名檔' : '上傳簽名檔'}
          </Button>
        </Upload>

        <Text type="secondary" style={{ fontSize: 12 }}>
          支援圖片（PNG/JPG）或 PDF，將作為稽核報告電子簽名使用。
        </Text>
      </Space>
    </SectionCard>
  );
}

// ─── 電子文件區塊 ─────────────────────────────────────────────────────────────

/**
 * 電子文件子組件。
 * 多檔上傳 + 下載 + 刪除。
 */
function DocumentsSection({ userId, documents }: { userId: number; documents: StaffDocument[] }) {
  const qc = useQueryClient();
  // 記錄當前正在上傳的檔案進度（{ filename: pct }）
  const [uploadingFiles, setUploadingFiles] = useState<Record<string, number>>({});

  /** 刪除文件 Mutation */
  const deleteDoc = useMutation({
    mutationFn: (fid: number) => apiClient.delete(`/api/settings/staff/${userId}/documents/${fid}`),
    onSuccess: () => {
      message.success('電子文件已刪除');
      void qc.invalidateQueries({ queryKey: ['staff-profile', userId] });
    },
    onError: (err) => {
      message.error(err instanceof Error ? err.message : '刪除失敗');
    },
  });

  /**
   * 處理電子文件上傳：
   * 1. OSS 三步直傳（uploadFile scope='staff-doc'）
   * 2. 取得 fileId 後 POST /documents { fileId }
   */
  async function handleUpload(file: RcFile) {
    const key = file.name;
    setUploadingFiles((prev) => ({ ...prev, [key]: 0 }));
    try {
      const uploaded = await uploadFile(file, {
        scope: 'staff-doc',
        ownerType: 'staff',
        ownerId: userId,
        onProgress: (pct) => setUploadingFiles((prev) => ({ ...prev, [key]: pct })),
      });
      // 通知後端落庫
      await apiClient.post(`/api/settings/staff/${userId}/documents`, { fileId: uploaded.id });
      message.success(`${file.name} 已上傳`);
      void qc.invalidateQueries({ queryKey: ['staff-profile', userId] });
    } catch (err) {
      message.error(err instanceof Error ? err.message : `${file.name} 上傳失敗`);
    } finally {
      setUploadingFiles((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }
    return false;
  }

  const columns: ColumnsType<StaffDocument> = [
    {
      title: '檔案名稱',
      dataIndex: 'filename',
      ellipsis: true,
      render: (v: string) => (
        <span style={{ fontFamily: 'monospace', fontSize: 13, color: color.text.secondary }}>
          {v}
        </span>
      ),
    },
    {
      title: '大小',
      dataIndex: 'sizeBytes',
      width: 90,
      render: (v: number) => formatBytes(v),
    },
    {
      title: '上傳時間',
      dataIndex: 'createdAt',
      width: 160,
      render: (v: string) => formatDateTime(v),
    },
    {
      title: '操作',
      key: '_actions',
      width: 90,
      fixed: 'right',
      render: (_, record) => (
        <Space size={4}>
          <Button
            type="text"
            size="small"
            icon={<Download size={13} />}
            onClick={() => void downloadFile(record.id)}
          />
          <Popconfirm
            title="確定刪除此電子文件？"
            onConfirm={() => deleteDoc.mutate(record.id)}
            okText="刪除"
            cancelText="取消"
            okButtonProps={{ danger: true }}
          >
            <Button type="text" size="small" danger icon={<Trash2 size={13} />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // 正在上傳的檔案清單（顯示進度條）
  const uploadingList = Object.entries(uploadingFiles);

  return (
    <SectionCard
      title="電子檔案"
      icon={<FileText size={14} />}
      padless
      actions={
        <Upload
          showUploadList={false}
          multiple
          beforeUpload={(file) => {
            void handleUpload(file);
            return false;
          }}
        >
          <Button type="primary" size="small" icon={<Plus size={13} />}>
            上傳文件
          </Button>
        </Upload>
      }
    >
      {/* 上傳中的進度列表 */}
      {uploadingList.length > 0 && (
        <div style={{ padding: '8px 16px', borderBottom: `1px solid ${color.border.subtle}` }}>
          {uploadingList.map(([name, pct]) => (
            <div key={name} style={{ marginBottom: 4 }}>
              <Text style={{ fontSize: 12 }}>{name}</Text>
              <Progress percent={Math.round(pct)} size="small" status="active" />
            </div>
          ))}
        </div>
      )}

      <Table<StaffDocument>
        rowKey="id"
        size="small"
        dataSource={documents}
        columns={columns}
        pagination={false}
        locale={{ emptyText: '尚無電子文件' }}
        scroll={{ x: 500 }}
      />
    </SectionCard>
  );
}

// ─── 主組件 ──────────────────────────────────────────────────────────────────

/**
 * 電子檔案 Tab 主組件。
 * 上半：簽名檔管理。
 * 下半：員工電子文件列表（含上傳/下載/刪除）。
 */
export function DocumentsTab({ userId, signature, documents }: DocumentsTabProps) {
  return (
    <Space direction="vertical" size={16} style={{ display: 'flex' }}>
      <SignatureSection userId={userId} signature={signature} />
      <DocumentsSection userId={userId} documents={documents} />
    </Space>
  );
}
